'use client';

import { motion } from 'motion/react';
import { usePathname, useSearchParams } from 'next/navigation';
import * as React from 'react';

function isModifiedEvent(event: MouseEvent) {
  return event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;
}

function shouldHandleLinkClick(anchor: HTMLAnchorElement, event: MouseEvent) {
  if (event.defaultPrevented) return false;
  if (event.button !== 0) return false; // left click only
  if (isModifiedEvent(event)) return false;
  if (anchor.target && anchor.target !== '_self') return false;
  if (anchor.hasAttribute('download')) return false;

  const href = anchor.getAttribute('href');
  if (!href) return false;
  if (href.startsWith('#')) return false;
  if (href.startsWith('mailto:') || href.startsWith('tel:')) return false;

  const url = new URL(anchor.href);
  return url.origin === window.location.origin;
}

function getUrlKeyFromAnchor(anchor: HTMLAnchorElement) {
  const url = new URL(anchor.href);
  return `${url.pathname}?${url.search.replace(/^\?/, '')}`;
}

type BarPhase = 'hidden' | 'loading' | 'finishing';

export default function NavigationOverlay() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [phase, setPhase] = React.useState<BarPhase>('hidden');
  const [progress, setProgress] = React.useState(0);

  const showDelayTimerRef = React.useRef<number | null>(null);
  const tickTimerRef = React.useRef<number | null>(null);
  const hideTimerRef = React.useRef<number | null>(null);

  const navigationTokenRef = React.useRef(0);

  const hasStartedForCurrentNavigationRef = React.useRef(false);

  const lastCommittedKeyRef = React.useRef<string>('');

  const pointerStartPositionRef = React.useRef<{ x: number; y: number } | null>(
    null
  );
  const didPointerMoveBeyondThresholdRef = React.useRef(false);

  const clearTimers = React.useCallback(() => {
    if (showDelayTimerRef.current !== null) {
      window.clearTimeout(showDelayTimerRef.current);
      showDelayTimerRef.current = null;
    }
    if (tickTimerRef.current !== null) {
      window.clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const start = React.useCallback(() => {
    if (hasStartedForCurrentNavigationRef.current) {
      return;
    }
    hasStartedForCurrentNavigationRef.current = true;

    navigationTokenRef.current += 1;
    const navigationToken = navigationTokenRef.current;

    clearTimers();

    showDelayTimerRef.current = window.setTimeout(() => {
      if (navigationTokenRef.current !== navigationToken) {
        return;
      }

      setProgress(8);
      setPhase('loading');

      tickTimerRef.current = window.setInterval(() => {
        if (navigationTokenRef.current !== navigationToken) {
          return;
        }

        setProgress((current) => {
          const cap = 85;
          if (current >= cap) return current;

          const remaining = cap - current;
          const step = Math.max(0.6, remaining * 0.08);

          return Math.min(cap, current + step);
        });
      }, 120);
    }, 120);
  }, [clearTimers]);

  const finish = React.useCallback(() => {
    navigationTokenRef.current += 1;
    clearTimers();

    setPhase((currentPhase) => {
      if (currentPhase === 'hidden') return 'hidden';
      return 'finishing';
    });

    setProgress(100);

    hideTimerRef.current = window.setTimeout(() => {
      setPhase('hidden');
      setProgress(0);
    }, 220);
  }, [clearTimers]);

  React.useEffect(() => {
    const committedKey = `${pathname}?${searchParams.toString()}`;
    lastCommittedKeyRef.current = committedKey;

    finish();
    hasStartedForCurrentNavigationRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams]);

  React.useEffect(() => {
    const movementThresholdPixels = 8;

    const onPointerDownCapture = (event: PointerEvent) => {
      pointerStartPositionRef.current = { x: event.clientX, y: event.clientY };
      didPointerMoveBeyondThresholdRef.current = false;
    };

    const onPointerMoveCapture = (event: PointerEvent) => {
      if (!pointerStartPositionRef.current) {
        return;
      }

      const deltaX = Math.abs(
        event.clientX - pointerStartPositionRef.current.x
      );
      const deltaY = Math.abs(
        event.clientY - pointerStartPositionRef.current.y
      );

      if (
        deltaX > movementThresholdPixels ||
        deltaY > movementThresholdPixels
      ) {
        didPointerMoveBeyondThresholdRef.current = true;
      }
    };

    const onPointerUpCapture = () => {
      pointerStartPositionRef.current = null;
    };

    document.addEventListener('pointerdown', onPointerDownCapture, true);
    document.addEventListener('pointermove', onPointerMoveCapture, true);
    document.addEventListener('pointerup', onPointerUpCapture, true);
    document.addEventListener('pointercancel', onPointerUpCapture, true);

    return () => {
      document.removeEventListener('pointerdown', onPointerDownCapture, true);
      document.removeEventListener('pointermove', onPointerMoveCapture, true);
      document.removeEventListener('pointerup', onPointerUpCapture, true);
      document.removeEventListener('pointercancel', onPointerUpCapture, true);
    };
  }, []);

  React.useEffect(() => {
    const onClickCapture = (event: MouseEvent) => {
      if (didPointerMoveBeyondThresholdRef.current) {
        didPointerMoveBeyondThresholdRef.current = false;
        return;
      }

      const target = event.target as Element | null;
      const anchor = target?.closest('a') as HTMLAnchorElement | null;
      if (!anchor) return;

      if (!shouldHandleLinkClick(anchor, event)) {
        return;
      }

      const currentKey = `${window.location.pathname}?${window.location.search.replace(
        /^\?/,
        ''
      )}`;
      const targetKey = getUrlKeyFromAnchor(anchor);

      if (targetKey === currentKey) {
        start();
        window.setTimeout(() => {
          finish();
          hasStartedForCurrentNavigationRef.current = false;
        }, 400);
        return;
      }

      start();
    };

    const onPopState = () => {
      const currentKey = `${window.location.pathname}?${window.location.search.replace(
        /^\?/,
        ''
      )}`;
      if (currentKey === lastCommittedKeyRef.current) {
        return;
      }

      start();
    };

    document.addEventListener('click', onClickCapture, true);
    window.addEventListener('popstate', onPopState);

    return () => {
      document.removeEventListener('click', onClickCapture, true);
      window.removeEventListener('popstate', onPopState);
      clearTimers();
    };
  }, [clearTimers, finish, start]);

  const isVisible = phase !== 'hidden';
  const scaleXValue = Math.max(0, Math.min(1, progress / 100));

  return (
    <motion.div
      aria-live="polite"
      aria-busy={isVisible ? 'true' : 'false'}
      className="pointer-events-none fixed inset-x-0 top-0 z-100 h-1"
      initial={false}
      animate={isVisible ? 'visible' : 'hidden'}
      variants={{
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.12 } },
      }}
    >
      <motion.div
        className="from-brand-quaternary to-brand-primary h-full origin-left bg-linear-to-r"
        initial={false}
        animate={{
          scaleX: scaleXValue,
          opacity: phase === 'finishing' ? 0 : 1,
        }}
        transition={{
          scaleX: {
            type: 'tween',
            ease: 'easeOut',
            duration: scaleXValue === 0 ? 0 : 0.18,
          },
          opacity: { type: 'tween', ease: 'easeOut', duration: 0.2 },
        }}
      />
    </motion.div>
  );
}
